import React, { useState, useEffect, useRef } from "react";
import { FixedSizeList as List } from "react-window"; // Import react-window for virtualization
import { Label } from "./Label";

const qualificationsChunkSize = 50; // Number of qualifications to load per chunk

const LazyLoadingSelectQualification = ({
  qualifications,
  formData,
  setFormData,
}) => {
  const [displayedQualifications, setDisplayedQualifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Track search term
  const [filteredQualifications, setFilteredQualifications] =
    useState(qualifications);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const selectQualif = (qualif) => {
    setSearchTerm(qualif); // Afficher la qualification sélectionnée
    setShowDropdown(false); // Fermer la dropdown
    setFormData({ ...formData, qualification: qualif });
  };

  // Initialiser avec la valeur existante
  useEffect(() => {
    if (formData?.qualification && !searchTerm) {
      setSearchTerm(formData.qualification);
    }
  }, [formData?.qualification]);

  // Filter qualifications based on search input
  useEffect(() => {
    const filtered = qualifications.filter((qualification) =>
      qualification.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredQualifications(filtered); // Update filtered qualifications
    setCurrentIndex(0); // Reset index when search term changes
    setDisplayedQualifications(filtered.slice(0, qualificationsChunkSize)); // Load the first chunk
  }, [searchTerm, qualifications]); // Re-run whenever searchTerm or qualifications changes

  // Load more qualifications when the user scrolls
  const handleScroll = () => {
    if (!dropdownRef.current) return;

    const bottom =
      dropdownRef.current.scrollHeight ===
      dropdownRef.current.scrollTop + dropdownRef.current.clientHeight;
    if (bottom && currentIndex < filteredQualifications.length) {
      loadMoreQualifications();
    }
  };

  const loadMoreQualifications = () => {
    setIsLoading(true);

    setTimeout(() => {
      const newQualifications = filteredQualifications.slice(
        currentIndex,
        currentIndex + qualificationsChunkSize,
      );
      setDisplayedQualifications((prev) => [...prev, ...newQualifications]);
      setCurrentIndex(currentIndex + qualificationsChunkSize);
      setIsLoading(false);
    }, 300); // Simulate a delay, adjust as needed
  };

  // Render each row in the dropdown list
  const renderRow = ({ index, style }) => {
    const qualification = displayedQualifications[index];
    return (
      <div
        onClick={() => selectQualif(qualification)}
        style={style}
        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${searchTerm === qualification && "bg-purple-200 text-purple-600"}`}
      >
        {qualification}
      </div>
    );
  };

  return (
    <div>
      <Label>Poste</Label>

      <div
        className="relative border border-gray-300 rounded-md"
        onScroll={handleScroll} // Attach the scroll handler here
        ref={dropdownRef}
      >
        <input
          type="text"
          placeholder="Rechercher un poste"
          value={searchTerm} // Bind search input to state
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true); // Ouvrir la dropdown quand l'utilisateur tape
          }}
          onFocus={() => setShowDropdown(true)} // Ouvrir la dropdown au focus
          className="w-full border-b border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {filteredQualifications.length > 0 && showDropdown && searchTerm.length > 0 && (
          <List
            className="scrollbar-none"
            height={100} // Adjust the height of the dropdown
            itemCount={displayedQualifications.length}
            itemSize={40} // Adjust the size of each item
            width="100%" // Make sure it takes full width
          >
            {renderRow}
          </List>
        )}

        {isLoading && (
          <div className="text-center text-gray-500 mt-2">Chargement...</div>
        )}
      </div>
    </div>
  );
};

export default LazyLoadingSelectQualification;
